import type { OptionsType } from '@openx/types/options';
import type { SelectRateFormState } from '@openx/types/targeting/selectRate';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyFilterMessage } from '../EmptyFilterMessage/EmptyFilterMessage';
import { TargetingDrawer } from '../TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../TargetingDrawer/useDrawer';
import { defaultParams } from './constants';
import { Items } from './Items';
import { SelectRateReadOnly } from './SelectRateReadOnly';
import { TopBox } from './TopBox';

interface SelectRateProps extends TargetingProps<SelectRateFormState> {
  placeholder: string;
  filterTitle: string;
  dataTest?: string;
  optionsFetch: typeof OptionsType.VIEWABILITY | typeof OptionsType.VIEW_THROUGH_RATE;
  description?: string;
  userChangedInitialValue?: boolean;
}

export const SelectRate = memo(function SelectRate({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  filterTitle,
  placeholder,
  optionsFetch,
  dataTest,
  description,
  userChangedInitialValue,
}: SelectRateProps) {
  const { t } = useTranslation();

  const initState = useMemo(
    () => (targetingParams ? cloneDeep(targetingParams) : { ...defaultParams }),
    [targetingParams]
  );
  const [params, updateParams] = useState<SelectRateFormState>(initState);
  const isDataUpdated = useMemo(() => !isEqual(initState, params), [initState, params]);

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdate(field, params.val ? params : null),
    restoreParams: () => updateParams({ ...initState }),
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdate(field, null);
    updateParams({ ...params, val: '' });
  }, [onFieldUpdate, params, field]);

  const onChange = useCallback((val: string | null) => updateParams(params => ({ op: params.op, val })), []);

  const isEmpty = !params.val;

  return (
    <>
      <SelectRateReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue || false}
        readonly={readonly}
        targetingParams={initState}
        onOpenDrawerClick={onDrawerOpen}
        onRemoveClick={onRemoveAll}
        filterTitle={filterTitle}
        optionsFetch={optionsFetch}
        dataTest={dataTest}
        description={description}
      />
      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <TopBox value={params.val} onChange={onChange} placeholder={placeholder} optionsFetch={optionsFetch} />
          {isEmpty && <EmptyFilterMessage text={t('No filters applied.')} />}
          {!isEmpty && (
            <Items
              title={filterTitle}
              targetingParams={params}
              readonly={false}
              onChange={updateParams}
              field={field}
              optionsFetch={optionsFetch}
              dataTest={dataTest}
            />
          )}
        </TargetingDrawer>
      )}
    </>
  );
});
